import { Fragment, Component } from 'react';
import classNames from 'classnames';
import * as ss from 'simple-statistics';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import d3Tip from 'd3-tip';
import * as d3 from 'd3';
import { NullStateCard } from '@hc/component-lib/hclib/components/molecules/null-state-card';

import { dollarsFormatter } from 'legacy/utils/formatters';
import { NULL_STATE_WARNINGS } from 'legacy/appstore/constants';

import subjectLogo from 'legacy/assets/images/icons/subject.png';
import subjectUnavailable from 'legacy/assets/svg-inline/subject-unavailable.svg';
import SVGInline from 'react-svg-inline';

import Loading from 'legacy/components/generic/Loading';

import { PropertyExplorerValuationChart as theme } from 'legacy/css-modules';

class PropertyExplorerValuationChart extends Component {
  initialized = false;
  minimumNumberOfComps = 15;
  nonDisclosureStatePriceKey = 'lastListPriceAdjusted';

  state = {
    info: <div />
  };

  icon = null;

  componentDidMount = () => {
    this.renderChart();
  };

  componentDidUpdate = () => {
    this.renderChart();
  };

  /**
   * Calculates the {pct} percentile from the given values
   * @param values
   * @param pct
   * @returns {number}
   */
  calculatePercentile = (values, pct) => {
    const index = (pct / 100) * (values.length - 1);
    let p;
    if (index % 1 === 0) {
      p = values[index];
    } else {
      let i = Math.floor(index);
      p = values[i] + (values[i + 1] - values[i]) * (index - i);
    }
    return Math.round(p);
  };

  /**
   * Returns a readable number with ordinal indicator appended, i.e. 91st
   * @param i
   * @returns {string}
   */
  ordinalIndicator = (i) => {
    let j = i % 10;
    let k = i % 100;
    if (j === 1 && k !== 11) {
      return i + 'st';
    }
    if (j === 2 && k !== 12) {
      return i + 'nd';
    }
    if (j === 3 && k !== 13) {
      return i + 'rd';
    }
    return i + 'th';
  };

  /**
   * Returns rounded value per sqft else nothing
   * @param v
   * @returns {number}
   */
  getValuePerSqft = (v) => {
    return v ? Math.round(v) : v;
  };

  /**
   * Returns an array of all values needed for the domain of the x-axis
   * @returns {number[]}
   */
  getDomainValues = () => {
    // account for AVM $ being > or < all comps
    let domainValues = this.prices.slice();
    domainValues.push(this.avm.value);
    return domainValues;
  };

  /**
   * Determines if we've recomputed AVM values since the last draw.
   * @param avmKey
   * @param avm
   */
  hasRecomputed = (avmKey, avm) => {
    return (
      this.initialized &&
      (avmKey !== this.avmKey || avm.value !== this.avm.value)
    );
  };

  /**
   * Returns a list of comp prices from the given list of comp objects
   * @param comps
   */
  getCompPrices = (comps, isInNonDisclosureState) => {
    let priceKey = isInNonDisclosureState
      ? this.nonDisclosureStatePriceKey
      : this.props.priceKey;
    return comps.map((c) => c[priceKey]).filter((c) => c);
  };

  /**
   * Returns a list of usable comp prices for the histogram.
   * Removes prices > 2 std dev from the mean.
   * @param comps
   * @param isInNonDisclosureState
   */
  getUsableCompPrices = (comps, isInNonDisclosureState) => {
    // list of adjusted prices (sales or list)
    let prices = this.getCompPrices(comps);
    let minComp = prices.length > 0 ? ss.min(prices) : null;
    let normalizedComps = map(
      prices,
      (p) => (p - minComp) / (ss.max(prices) - minComp)
    );
    let zScores = map(normalizedComps, (p) =>
      Math.abs(
        (p - ss.mean(normalizedComps)) / ss.standardDeviation(normalizedComps)
      )
    );
    // filter prices more than 2 standard deviations away from the mean
    return sortBy(prices.filter((c, i) => zScores[i] < 2.0));
  };

  /**
   * Returns the number of bins to create for the histogram.
   * @returns {number}
   */
  getNumBins = () => {
    let numBins;
    if (this.prices.length >= 40) {
      numBins = 12;
    } else if (this.prices.length >= 30) {
      numBins = 10;
    } else if (this.prices.length >= 20) {
      numBins = 8;
    } else {
      numBins = 3;
    }
    return numBins;
  };

  /**
   * Initializes the chart.
   */
  initChart = () => {
    this.initialized = true;

    const node = this.node;

    const { avmKey, avm, comps, isInNonDisclosureState } = this.props;
    this.setValues(avmKey, avm, comps, isInNonDisclosureState);

    if (this.svg) {
      this.svg.selectAll('*').remove();
    }

    this.valueFormatter = d3.format(',');
    this.countFormatter = d3.format(',.0f');
    this.margin = { top: 75, right: 40, bottom: 50, left: 40 };
    this.width = 800 - this.margin.left - this.margin.right;
    this.height = 400 - this.margin.top - this.margin.bottom;

    this.svg = d3
      .select(node)
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom);

    // creating a content element to place chart inside
    // which will incorporate margins
    this.content = this.svg
      .append('g')
      .attr('class', theme.Content)
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    // create the scale for the x-axis
    this.x = d3
      .scaleLinear()
      .domain(d3.extent(this.getDomainValues()))
      .rangeRound([0, this.width])
      .nice();

    // create the y-axis
    this.y = d3.scaleLinear().range([this.height, 0]);

    this.maxHeight = this.height + this.margin.top;
    d3.scaleLinear().range([this.maxHeight, 0]);

    // Scale the range of the data in the y domain
    this.y.domain([0, d3.max(this.bins, (d) => d.length)]);
    // we'll store any created tooltips in here for easy access
    this.tooltips = {};
  };

  /**
   * Setter for values used for building out the plot.
   * This is called when first initializing the chart and then each time the AVM is updated.
   * @param avmKey
   * @param avm
   * @param comps
   * @param isInNonDisclosureState
   */
  // updates data properties to trigger d3 enters
  setValues = (avmKey, avm, comps, isInNonDisclosureState) => {
    this.avmKey = avmKey;
    this.avm = avm;

    this.prices = this.getUsableCompPrices(comps, isInNonDisclosureState);
    this.avg = Math.round(ss.mean(this.prices));
    this.min = ss.min(this.prices);
    this.max = ss.max(this.prices);
    this.valueCount = this.prices.length;
    // create the bins used for the histogram bars
    this.bins = this.createBins();
  };

  /**
   * Creates the bins for the histogram
   * @returns {*}
   */
  createBins = () => {
    const numBins = this.getNumBins();
    const thresholds = d3.range(
      this.min,
      this.max,
      (this.max - this.min) / numBins
    );
    return d3
      .histogram()
      .thresholds(map(thresholds, (x) => Math.floor(x / 100) * 100))(
      this.prices
    );
  };

  /**
   * Creates the rectangles that represent a histogram
   */
  createHistogram = () => {
    const rect = this.content
      .selectAll('rect')
      .data(this.bins)
      .enter()
      .append('g')
      .attr('transform', (d) => {
        return 'translate(' + this.x(d.x0) + ',' + this.y(d.length) + ')';
      });

    rect
      .append('rect')
      .attr('id', (d, i) => 'rect-' + i)
      .attr('class', theme.Bar)
      .attr('x', 0)
      .attr('width', (d) => this.x(d.x1) - this.x(d.x0) - 1)
      .attr('height', (d) => this.height - this.y(d.length))
      .on('mouseover', this.handleBarMouseOver)
      .on('mouseout', this.handleBarMouseOut);

    rect
      .append('text')
      .attr('id', (d, i) => 'rect-text-' + i)
      .attr('font-weight', 'bold')
      .attr('font-family', 'Avenir')
      .attr('font-size', '12px')
      .attr('y', -10)
      .attr('x', (d) => {
        return (this.x(d.x1) - this.x(d.x0)) / 2;
      })
      .attr('text-anchor', 'middle')
      .text((d) => this.countFormatter(d.length))
      .style('opacity', 0);
  };

  /**
   * Adds the x-axis to the bottom of the SVG and formats the ticks
   */
  addXAxis = () => {
    // add the x Axis
    let xAxis = this.content
      .append('g')
      .attr('transform', `translate(0, ${this.height})`)
      .call(d3.axisBottom(this.x).tickFormat((val) => dollarsFormatter(val)));

    xAxis.select('path').attr('stroke', '#D6D6D6').attr('stroke-width', '3px');

    let tickCount = xAxis.selectAll('line').size();
    xAxis
      .selectAll('line')
      .filter((d, i) => i === 0 || i === tickCount - 1)
      .attr('stroke', '#D6D6D6')
      .attr('y2', 15)
      .attr('stroke-width', '3px');

    xAxis
      .selectAll('.tick')
      .filter((d, i) => i > 0 && i < tickCount - 1)
      .attr('opacity', 0);

    xAxis
      .selectAll('text')
      .attr('font-family', 'Avenir')
      .attr('font-size', '12')
      .attr('transform', 'translate(0, 10)')
      .attr('font-weight', 'bold');
  };

  /**
   * Adds the dotted line and text for average sale price
   */
  displayAverageSalePrice = () => {
    // add dotted line for avg adj sales price percentile
    this.content
      .append('line')
      .attr('x1', this.x(this.avg))
      // extend towards the Average Sale Price text above the chart
      .attr('y1', -5)
      .attr('x2', this.x(this.avg))
      .attr('y2', this.height)
      .attr('id', 'avg-adj-price')
      .attr('fill', 'none')
      .style('stroke', 'black')
      .attr('stroke-dashoffset', 0)
      .attr('stroke-dasharray', '9, 9')
      .style('stroke-width', 3);

    const averagePositionX = this.x(this.avg);
    this.content
      .append('text')
      .attr('class', theme.AverageSalePriceLabel)
      // to bring it above the highest y value
      .attr('transform', `translate(${averagePositionX}, -40)`)
      .attr('text-anchor', 'middle')
      .text('Average');

    this.content
      .append('text')
      .attr('class', theme.AverageSalePriceLabel)
      // so it's underneath the "Average" text above
      .attr('transform', `translate(${averagePositionX}, -25)`)
      .attr('text-anchor', 'middle')
      .text('Sale Price');

    this.content
      .append('text')
      .attr('class', theme.AverageSalePriceValue)
      // add space between x-axis and $ value
      .attr('transform', `translate(${averagePositionX}, ${this.height + 26})`)
      .attr('text-anchor', 'middle')
      .attr('font-weight', 'bold')
      .attr('font-size', '12px')
      .text(dollarsFormatter(this.avg));
  };

  /**
   * Display the icon for the HouseCanary AVM
   */
  displayAvmValue = () => {
    if (!this.props.avm.value) {
      return;
    }
    this.houseIcon = this.content
      .append('image')
      .attr('class', theme.HcIcon)
      .attr('id', 'hc-avm-icon')
      .attr('xlink:href', subjectLogo)
      .attr('height', '70px')
      .attr('width', '70px')
      .on('mouseover', this.handleAvmMouseOver)
      .on('mouseout', this.handleAvmMouseOut);

    this.placeAvmValue();
  };

  /**
   * Places the house icon for HouseCanary AVM
   */
  placeAvmValue = () => {
    if (!this.props.avm.value) {
      return;
    }
    this.houseIcon
      .attr('opacity', 1)
      .attr(
        'transform',
        `translate(${this.x(this.avm.value) - 18.75}, ${this.height - 35})`
      );
  };

  /**
   * Sets text in the info panel to the left of the chart.
   * @param line1 - string for first line
   * @param line2 - string for second line
   */
  setInfo = (line1, line2) => {
    this.setState({
      info: (
        <div className={theme.InfoContent}>
          <p data-hc-name="similar-homes-sold-text">{line1}</p>
          <hr />
          <p data-hc-name="similar-homes-range-text">{line2}</p>
          <hr />
          <p data-hc-name="similar-homes-average-text">
            *Average sale price accounts for differences in the structure and
            location of individual properties.
          </p>
        </div>
      )
    });
  };

  /**
   * Sets the default text in the info panel.
   */
  setDefaultInfo = () => {
    let lowPercentile = this.calculatePercentile(this.prices, 5);
    let uprPercentile = this.calculatePercentile(this.prices, 95);
    let min = ss.min(this.prices);
    let max = ss.max(this.prices);

    this.setInfo(
      `90% of comparable properties sold between $${this.valueFormatter(
        lowPercentile
      )} and $${this.valueFormatter(uprPercentile)}.`,
      `The minimum sale price is $${this.valueFormatter(
        min
      )} and the maximum sale price is $${this.valueFormatter(max)}.`
    );
  };

  /**
   * Sets HouseCanary AVM info in the info panel.
   */
  setAvmInfo = () => {
    let value = this.avm.value;
    let percentile = Math.round(
      (this.prices.filter((p) => p < value).length / this.valueCount) * 100
    );
    let avgCompareText;
    if (value > this.avg) {
      avgCompareText = `$${this.valueFormatter(
        value - this.avg
      )} greater than the average comparable sale price`;
    } else {
      avgCompareText = `$${this.valueFormatter(
        this.avg - value
      )} less than the average comparable sale price`;
    }

    this.setInfo(
      `The subject value is $${this.valueFormatter(value)}.`,
      `It is ${avgCompareText} and in the ${this.ordinalIndicator(
        percentile
      )} percentile of all properties.`
    );
  };

  /**
   * Set info from a single histogram bar in the info panel.
   * @param d
   */
  setBarInfo = (d) => {
    this.setInfo(
      `${d.length} properties sold between $${this.valueFormatter(
        d.x0
      )} and $${this.valueFormatter(d.x1)}.`,
      `This represents ${Math.round(
        (d.length / this.valueCount) * 100
      )}% of total properties.`
    );
  };

  /**
   * Creates the tooltip for the subject AVM which is placed over the house icon.
   */
  createAvmTooltip = () => {
    if (!this.tooltips['avm']) {
      const tip = d3Tip()
        .attr('class', theme.Tooltip)
        .offset([-18, 6])
        .html(() => {
          return `
          <div class='${theme.TooltipContent}'>
            <div class='${theme.InfoRow}'>Subject Value</div>
            <div class='${theme.InfoRow}'>${dollarsFormatter(
            this.avm.value
          )}</div>
            <div><span>$${this.getValuePerSqft(
              this.avm.valPerSqft
            )}/sqft</span></div>
          </div>
        `;
        });
      tip.direction('n');
      this.content.call(tip);
      this.tooltips['avm'] = tip;
    }
  };

  /**
   * Threshold check to determine whether we build the plot or not.
   * Calling getUsableCompPrices to prevent issue when getUsableCompPrices returns an empty list
   * where simple-statistics throws an error
   * @param isInNonDisclosureState {boolean} use passed in value as instance value is not set initially
   * @returns {boolean}
   */
  hasMoreThanMinimumComps = (isInNonDisclosureState) => {
    return (
      this.getCompPrices(this.props.comps).length >=
        this.minimumNumberOfComps &&
      this.getUsableCompPrices(this.props.comps, isInNonDisclosureState)
        .length > this.minimumNumberOfComps
    );
  };

  renderChart = () => {
    const { isLoading, avmKey, avm, comps, isInNonDisclosureState } =
      this.props;
    if (!this.hasMoreThanMinimumComps(isInNonDisclosureState) || isLoading) {
      return;
    }

    // we're drawing a new chart or we've recomputed AVMs since the last draw
    if (this.initialized === false || this.hasRecomputed(avmKey, avm)) {
      this.initChart();
      this.createHistogram();
      this.addXAxis();
      this.displayAverageSalePrice();
      this.displayAvmValue();
      this.setDefaultInfo();
      this.createAvmTooltip();
    } else {
      this.setValues(avmKey, avm, comps, isInNonDisclosureState);
      this.placeAvmValue();
      this.createAvmTooltip();
    }
  };

  /**
   * Handles mouseover for the house icon representing the HouseCanary AVM
   */
  handleAvmMouseOver = () => {
    this.handlePointMouseOver('avm');
    this.setAvmInfo();
  };

  /**
   * Handles mouseout for the house icon representing the HouseCanary AVM
   */
  handleAvmMouseOut = () => {
    this.handlePointMouseOut('avm');
    this.setDefaultInfo();
  };

  /**
   * Handles showing tooltip for all of the AVMs
   * @param ptName - str name of the point that's been hovered over
   */
  handlePointMouseOver = (ptName) => {
    if (this.tooltips[ptName]) {
      this.tooltips[ptName].show();
    }
  };

  /**
   * Handles hiding tooltip for all of the AVMs
   * @param ptName - str name of the point that's been hovered over
   */
  handlePointMouseOut = (ptName) => {
    if (this.tooltips[ptName]) {
      // SECURITY: false positive, this is a library method @jnettleman
      // eslint-disable-next-line scanjs-rules/call_hide
      this.tooltips[ptName].hide();
    }
  };

  /**
   * Handles mouseover event for individual bar of the histogram
   * @param d - individual bar object
   * @param i - index of the bar
   */
  handleBarMouseOver = (d, i) => {
    d3.select('#rect-text-' + i).style('opacity', 1);
    this.setBarInfo(d);
  };

  /**
   * Handles mouseout event for individual bar of the histogram
   * @param d - individual bar object
   * @param i - index of the bar
   */
  handleBarMouseOut = (d, i) => {
    d3.select('#rect-text-' + i).style('opacity', 0);
    this.setDefaultInfo();
  };

  render() {
    const { isLoading } = this.props;
    const hasEnoughComps = this.hasMoreThanMinimumComps();
    return hasEnoughComps || isLoading ? (
      <Fragment>
        <Loading loading={isLoading}>
          <div className={theme.PropertyExplorerValuation}>
            <div className={theme.PropertyExplorerValuationInfoSection}>
              <div
                className={theme.PropertyExplorerValuationInfoPanel}
                data-hc-name="similar-homes-chart-panel"
              >
                {this.state.info}
              </div>
              {!this.props.avm.value && (
                <div className={theme.PropertyExplorerValuationUnavailable}>
                  <div
                    className={theme.PropertyExplorerValuationUnavailableText}
                  >
                    <SVGInline svg={subjectUnavailable} />
                    <span>{NULL_STATE_WARNINGS.noAvmHc.title}</span>
                  </div>
                </div>
              )}
            </div>
            <div
              className={theme.PropertyExplorerValuationChart}
              data-hc-name="similar-homes-chart"
            >
              <svg
                ref={(node) => {
                  this.node = node;
                }}
              />
            </div>
          </div>
        </Loading>
      </Fragment>
    ) : this.props.chartType === 'nearby' ? (
      <NullStateCard
        theme={theme}
        title={NULL_STATE_WARNINGS.similarHomesChart.title}
      >
        {NULL_STATE_WARNINGS.similarHomesChart.message}
      </NullStateCard>
    ) : (
      <NullStateCard className={theme.NullStateCard} title="Not Enough Comps">
        Change your comp filters to see this chart.
      </NullStateCard>
    );
  }
}

const PropertyExplorerValuationSection = (props) => {
  return (
    <div>
      <div className={classNames(theme.PropertyExplorerValuationSection)}>
        <PropertyExplorerValuationChart {...props} />
      </div>
    </div>
  );
};

export default PropertyExplorerValuationSection;
